import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import ReadingProgressBar from '../components/ReadingProgressBar';
import { CommentSection } from '../components/CommentSection';
import { ReadNext } from '../components/ReadNext';
import { TipTapContentRenderer } from '../components/TipTapContentRenderer';
import { BackToArticlePill } from '../components/BackToArticlePill';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Clock, 
  HandMetal, 
  Bookmark, 
  BookmarkCheck, 
  Share2, 
  Twitter,
  Linkedin,
  Link as LinkIcon,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Library,
  Eye,
  PenLine,
  Trash2,
  EyeOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

import { API, authFetch } from '../lib/api';

export const ArticlePage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  const [article, setArticle] = useState(null);
  const [author, setAuthor] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isClapping, setIsClapping] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [articleId]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/articles/${articleId}`);
      
      if (response.ok) {
        const data = await response.json();
        setArticle(data);
        fetchAuthor(data.author_id);
        fetchComments();
      } else if (response.status === 404) {
        navigate('/');
        toast.error('Article not found');
      }
    } catch (error) {
      console.error('Failed to fetch article:', error);
      toast.error('Failed to load article');
    }
    setLoading(false);
  };

  const fetchAuthor = async (authorId) => {
    try {
      const response = await authFetch(`${API}/users/${authorId}`);
      if (response.ok) {
        const data = await response.json();
        setAuthor(data);
      }
    } catch (error) {
      console.error('Failed to fetch author:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(`${API}/articles/${articleId}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleClap = async () => {
    if (!user) {
      login();
      return;
    }

    setIsClapping(true);
    try {
      const response = await authFetch(`${API}/articles/${articleId}/clap`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        setArticle(prev => ({ 
          ...prev, 
          claps_count: data.claps_count,
          has_clapped: true 
        }));
      }
    } catch (error) {
      toast.error('Failed to clap');
    }
    setIsClapping(false);
  };

  const handleBookmark = async () => {
    if (!user) {
      login();
      return;
    }

    try {
      const method = article.is_bookmarked ? 'DELETE' : 'POST';
      const response = await authFetch(`${API}/articles/${articleId}/bookmark`, {
        method,
      });

      if (response.ok) {
        setArticle(prev => ({ ...prev, is_bookmarked: !prev.is_bookmarked }));
        toast.success(article.is_bookmarked ? 'Removed from bookmarks' : 'Added to bookmarks');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleFollow = async () => {
    if (!user) {
      login();
      return;
    }

    if (!author) return;

    try {
      const method = author.is_following ? 'DELETE' : 'POST';
      const response = await authFetch(`${API}/users/${author.user_id}/follow`, {
        method,
      });

      if (response.ok) {
        setAuthor(prev => ({ 
          ...prev, 
          is_following: !prev.is_following,
          followers_count: prev.is_following ? prev.followers_count - 1 : prev.followers_count + 1
        }));
        toast.success(author.is_following ? 'Unfollowed' : 'Following');
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article?.title || '';
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      copy: url
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCommentAdded = (comment, parentId = null) => {
    if (parentId) {
      setComments(prev => prev.map(c => 
        c.comment_id === parentId 
          ? { ...c, replies: [...(c.replies || []), { ...comment, replies: [] }] }
          : c
      ));
    } else {
      setComments(prev => [comment, ...prev]);
    }
    setArticle(prev => ({ ...prev, comments_count: prev.comments_count + 1 }));
  };

  const handleCommentDeleted = (commentId) => {
    setComments(prev => prev.filter(c => c.comment_id !== commentId));
    setArticle(prev => ({ ...prev, comments_count: Math.max(0, prev.comments_count - 1) }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(String(dateString).replace(' ', 'T'));
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleToggleVisibility = async () => {
    if (togglingVisibility) return;
    const nextPublished = !article.is_published;
    setTogglingVisibility(true);
    try {
      const res = await authFetch(`${API}/articles/${articleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: nextPublished })
      });
      if (res.ok) {
        const updated = await res.json();
        setArticle(prev => ({
          ...prev,
          is_published: updated.is_published,
          is_draft: updated.is_draft,
          published_at: updated.published_at ?? prev.published_at,
        }));
        toast.success(nextPublished ? 'Article is now public' : 'Article hidden from public');
      } else {
        let msg = 'Failed to update visibility';
        try { const e = await res.json(); msg = e.detail || msg; } catch (err) { console.error(err); }
        toast.error(msg);
      }
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
      toast.error('Failed to update visibility');
    }
    setTogglingVisibility(false);
  };

  const handleDeleteArticle = async () => {
    try {
      const res = await authFetch(`${API}/articles/${articleId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Article deleted');
        // Send the author back to their profile so they can see their remaining articles
        if (article?.author_id) {
          navigate(`/profile/${article.author_id}`);
        } else if (user?.user_id) {
          navigate(`/profile/${user.user_id}`);
        } else {
          navigate('/');
        }
      } else {
        let msg = 'Failed to delete';
        try { const e = await res.json(); msg = e.detail || msg; } catch (err) { console.error(err); }
        toast.error(msg);
      }
    } catch (err) {
      console.error('Failed to delete article:', err);
      toast.error('Failed to delete article');
    }
    setShowDeleteDialog(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="reading-container py-12">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="flex items-center gap-4 mb-8">
            <Skeleton className="w-12 h-12 rounded-full" />
            <div>
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <Skeleton className="h-64 w-full mb-8" />
          {Array(5).fill(0).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full mb-3" />
          ))}
        </div>
      </div>
    );
  }

  if (!article) return null;

  const pageTitle = article.seo_title || article.title;
  const pageDescription = article.seo_description || article.subtitle || `${article.title} by ${article.author_name}`;
  const canonicalUrl = `https://theblogsgrid.com/article/${article.article_id}`;
  const pageUrl = canonicalUrl;
  const pageImage = article.cover_image || 'https://theblogsgrid.com/og-image.png';
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: pageDescription,
    image: pageImage ? [pageImage] : undefined,
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at || article.published_at || article.created_at,
    author: {
      '@type': 'Person',
      name: article.author_name,
      url: `https://theblogsgrid.com/profile/${article.author_id}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'The Blogs Grid',
      url: 'https://theblogsgrid.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    keywords: Array.isArray(article.tags) ? article.tags.join(', ') : undefined,
    wordCount: article.reading_time ? article.reading_time * 200 : undefined,
  };

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="article-page">
      <Helmet>
        <title>{`${pageTitle} | The Blogs Grid`}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="The Blogs Grid" />
        {pageImage && <meta property="og:image" content={pageImage} />}
        <meta property="article:author" content={article.author_name} />
        {article.published_at && <meta property="article:published_time" content={article.published_at} />}
        {article.updated_at && <meta property="article:modified_time" content={article.updated_at} />}
        {Array.isArray(article.tags) && article.tags.map(t => (
          <meta key={t} property="article:tag" content={t} />
        ))}
        <meta name="twitter:card" content={pageImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>
      <ReadingProgressBar />
      <Navbar />
      <BackToArticlePill />
      
      <article className="reading-container py-8 sm:py-12 fade-up">
        {/* Author-only visibility banner */}
        {user?.user_id === article.author_id && !article.is_published && (
          <div
            className="mb-6 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100"
            data-testid="hidden-article-banner"
          >
            <EyeOff className="w-4 h-4 shrink-0" />
            <span>
              This article is <span className="font-medium">hidden from the public</span>. Only you can see it. Click <span className="font-medium">Make public</span> to publish it again.
            </span>
          </div>
        )}

        {/* Header */}
        <header className="mb-8">
          <h1 
            className="text-4xl sm:text-5xl font-bold mb-4 leading-tight"
            style={{ fontFamily: 'Fraunces, serif' }}
            data-testid="article-title"
          >
            {article.title}
          </h1>
          
          {article.subtitle && (
            <p className="text-xl text-muted-foreground mb-6">
              {article.subtitle}
            </p>
          )}

          {/* Author Info */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Link to={`/profile/${article.author_id}`}>
                <Avatar className="w-12 h-12">
                  <AvatarImage src={article.author_picture} alt={article.author_name} />
                  <AvatarFallback className="bg-accent text-accent-foreground">
                    {getInitials(article.author_name)}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <Link 
                    to={`/profile/${article.author_id}`}
                    className="font-medium hover:underline"
                    data-testid="author-name"
                  >
                    {article.author_name}
                  </Link>
                  {author && user?.user_id !== author.user_id && (
                    <Button
                      variant={author.is_following ? "outline" : "default"}
                      size="sm"
                      className="rounded-full h-7 text-xs"
                      onClick={handleFollow}
                      data-testid="follow-btn"
                    >
                      {author.is_following ? 'Following' : 'Follow'}
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{formatDate(article.published_at || article.created_at)}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {article.reading_time} min read
                  </span>
                  {article.views_count > 0 && (
                    <>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" />
                        {article.views_count.toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {user?.user_id === article.author_id && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1.5 press-scale"
                    onClick={() => navigate(`/write/${article.article_id}`)}
                    data-testid="edit-article-btn"
                  >
                    <PenLine className="w-3.5 h-3.5" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1.5 press-scale"
                    onClick={handleToggleVisibility}
                    disabled={togglingVisibility}
                    data-testid="toggle-visibility-btn"
                  >
                    {article.is_published ? (
                      <>
                        <EyeOff className="w-3.5 h-3.5" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="w-3.5 h-3.5" />
                        Make public
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1.5 press-scale text-destructive hover:text-destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    data-testid="delete-article-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBookmark}
                data-testid="bookmark-btn"
              >
                {article.is_bookmarked ? (
                  <BookmarkCheck className="w-5 h-5 text-accent" />
                ) : (
                  <Bookmark className="w-5 h-5" />
                )}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="share-btn">
                    <Share2 className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleShare('twitter')}>
                    <Twitter className="w-4 h-4 mr-2" />
                    Share on Twitter
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleShare('linkedin')}>
                    <Linkedin className="w-4 h-4 mr-2" />
                    Share on LinkedIn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleShare('copy')}>
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Cover Image */}
        {article.cover_image && (
          <div className="mb-10">
            <img
              src={article.cover_image}
              alt=""
              className="w-full rounded-lg"
              data-testid="cover-image"
            />
          </div>
        )}

        {/* Content */}
        <div className="article-body" data-testid="article-content">
          <TipTapContentRenderer content={article.content} />
        </div>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-border">
            <div className="flex flex-wrap gap-2">
              {article.tags.map(tag => (
                <Link key={tag} to={`/topic/${tag}`}>
                  <Badge 
                    variant="secondary" 
                    className="rounded-full hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  >
                    {tag}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Series Navigation */}
        {article.series_nav && (
          <div className="mt-10 pt-6 border-t border-border" data-testid="series-nav">
            <div className="bg-muted/30 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <Library className="w-4 h-4 text-accent" />
                <Link
                  to={`/series/${article.series_nav.series_id}`}
                  className="text-sm font-medium hover:text-accent transition-colors"
                >
                  {article.series_nav.series_title}
                </Link>
                <span className="text-xs text-muted-foreground ml-auto">
                  Part {article.series_nav.current_index + 1} of {article.series_nav.total}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-border rounded-full h-1 mb-4">
                <div
                  className="bg-accent h-1 rounded-full transition-all"
                  style={{ width: `${((article.series_nav.current_index + 1) / article.series_nav.total) * 100}%` }}
                />
              </div>

              <div className="flex items-stretch gap-3">
                {article.series_nav.prev ? (
                  <Link
                    to={`/article/${article.series_nav.prev.article_id}`}
                    className="flex-1 group flex items-center gap-2 p-3 rounded-md border border-border hover:border-accent/50 transition-colors"
                    data-testid="series-prev"
                  >
                    <ChevronLeft className="w-4 h-4 text-muted-foreground group-hover:text-accent shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Previous</p>
                      <p className="text-sm font-medium truncate group-hover:text-accent transition-colors">
                        {article.series_nav.prev.title}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}

                {article.series_nav.next ? (
                  <Link
                    to={`/article/${article.series_nav.next.article_id}`}
                    className="flex-1 group flex items-center justify-end gap-2 p-3 rounded-md border border-border hover:border-accent/50 transition-colors text-right"
                    data-testid="series-next"
                  >
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Next</p>
                      <p className="text-sm font-medium truncate group-hover:text-accent transition-colors">
                        {article.series_nav.next.title}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-accent shrink-0" />
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Clap Section */}
        <div className="mt-10 pt-6 border-t border-border flex items-center justify-center gap-4">
          <button
            onClick={handleClap}
            disabled={isClapping}
            className={`flex items-center gap-2 px-6 py-3 rounded-full border transition-all ${
              article.has_clapped 
                ? 'bg-accent text-accent-foreground border-accent' 
                : 'border-border hover:border-accent hover:text-accent'
            }`}
            data-testid="clap-btn"
          >
            <HandMetal className={`w-5 h-5 ${isClapping ? 'animate-bounce' : ''}`} />
            <span className="font-medium">{article.claps_count}</span>
          </button>
        </div>

        {/* Author Bio */}
        {author && (
          <div className="mt-10 p-6 bg-muted/30 rounded-lg">
            <div className="flex items-start gap-4">
              <Link to={`/profile/${author.user_id}`}>
                <Avatar className="w-16 h-16">
                  <AvatarImage src={author.picture} alt={author.name} />
                  <AvatarFallback className="bg-accent text-accent-foreground text-xl">
                    {getInitials(author.name)}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <Link 
                    to={`/profile/${author.user_id}`}
                    className="text-lg font-semibold hover:underline"
                  >
                    {author.name}
                  </Link>
                  {user?.user_id !== author.user_id && (
                    <Button
                      variant={author.is_following ? "outline" : "default"}
                      size="sm"
                      className="rounded-full"
                      onClick={handleFollow}
                    >
                      {author.is_following ? 'Following' : 'Follow'}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {author.followers_count} Followers · {author.articles_count} Articles
                </p>
                {author.bio && (
                  <p className="text-sm text-foreground/80">{author.bio}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Read next — semantically related articles */}
        <ReadNext articleId={articleId} />

        {/* Comments */}
        <CommentSection
          articleId={articleId}
          comments={comments}
          onCommentAdded={handleCommentAdded}
          onCommentDeleted={handleCommentDeleted}
        />
      </article>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{article.title}&rdquo; will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteArticle} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArticlePage;
